import { DecisionStore } from 'nomst/Stores/Decision/decision.store';
import { AmountCode, SidePanelListType, WorkflowStatusListType, ReportsView } from 'typings/enums';
import { WorkflowAction } from 'nomst/Stores/Decision/Constants/workflowAction';
import { User } from 'nomst/Stores/User/user';
import { IDecisionDispatcher } from 'typings/decision.dispatcher';
import { EmployeeSort } from 'nomst/Stores/Decision/Constants/employeeSort';

export class DecisionDispatcher implements IDecisionDispatcher {
    private readonly _decisionStore: Nullable<DecisionStore>;

    constructor(decisionStore: Nullable<DecisionStore>) {
        this._decisionStore = decisionStore;
    }

    dispatchWorkflowAction(workflowAction: WorkflowAction): void {
        this._decisionStore?.handleDispatchWorkflowAction(workflowAction);
    }

    async dispatchProceedWorkflowSubmissionAsync(
        currentUser: User,
        currentUserAsReviewerIndex: number,
        currentReviewerIndex: number): Promise<void> {
        await this._decisionStore?.handleProceedWorkflowSubmissionAsync(
            currentUser,
            currentUserAsReviewerIndex,
            currentReviewerIndex);
    }

    dispatchDebounceAmountChanged(
        employeeSourceId: string,
        amountCode: AmountCode,
        amountCycleId: number,
        amountValue: number): void {
        this._decisionStore?.handleDebounceAmountChanged(
            employeeSourceId,
            amountCode,
            amountCycleId,
            amountValue);
    }

    dispatchSidePanelToggled(sidePanelIsOpen: boolean): void {
        this._decisionStore?.handleSidePanelToggled(sidePanelIsOpen);
    }

    async dispatchEmployeePerformanceRatingChangedAsync(
        employeeSourceId: string,
        oldValue: string,
        newValue: string): Promise<void> {
        await this._decisionStore?.handleEmployeePerformanceRatingChangedAsync(
            employeeSourceId,
            oldValue,
            newValue);
    }

    dispatchResetView(): void {
        return this._decisionStore?.handleResetView();
    }

    dispatchSetTeamView(selectedView: ReportsView): void {
        this._decisionStore?.handleSetTeamView(selectedView);
    }

    dispatchJobAndDepartmentFilters(jobFilter: Nullable<string>, department: Nullable<string>, initialCall: boolean): void {
        this._decisionStore?.teamDecisionStore?.setSearchedFilter(jobFilter, department, initialCall);
    }

    dispatchPromotionFilter(value: boolean, initialCall: boolean = false): void {
        this._decisionStore?.teamDecisionStore?.handleDispatchPromotionOnlyChanged(value, initialCall);
    }

    dispatchSetTeamSelection(hasSelectedTeam: boolean): void {
        this._decisionStore?.handleSetTeamSelection(hasSelectedTeam);
    }

    async dispatchEmployeeSelection(employeeSourceId: Nullable<string>): Promise<void> {
        await this._decisionStore?.handleEmployeeSelection(employeeSourceId);
    }

    async dispatchSetWorkflowStatusManagersAsync(
        sidePanelListType: SidePanelListType,
        managerSourceIds: string[],
        workflowStatusType: WorkflowStatusListType): Promise<void> {
        await this._decisionStore?.handleSetupWorkFlowManager(
            sidePanelListType,
            managerSourceIds,
            workflowStatusType);
    }

    async dispatchSetOverBudgetManagerAsync(
        sidePanelListType: SidePanelListType,
        managerSourceIds: string[],
        budgetCycleId: number): Promise<void> {
        await this._decisionStore?.handleSetupOverBudgetManager(
            sidePanelListType,
            managerSourceIds,
            budgetCycleId);
    }

    dispatchSetBudgetNamesMapAsync(
        budgetNamesMap: Map<number, string>): void {
        return this._decisionStore?.handleSetUpBudgetNames(budgetNamesMap);
    }

    dispatchSetSelectedSortFormat(sort: EmployeeSort): void {
        return this._decisionStore?.teamDecisionStore?.setSelectedSortFormat(sort);
    }

    async dispatchPdfPrintServiceCall(): Promise<void> {
        await this._decisionStore?.teamDecisionStore.handleDispatchPdfPrintService();
    }

    dispatchResetPrintStatus(): void {
        this._decisionStore?.teamDecisionStore.resetPdfPrintStatus();
    }
}